import React__default, { useState, useMemo, useCallback, useEffect } from 'react';
import { Popover } from '@sravni/react-design-system/lib/Popover';
import { Space } from '@sravni/react-design-system/lib/Space';
import { Link } from '@sravni/react-design-system/lib/Typography';
import { useStyles, useDebounceValue, mergeRefs } from '@sravni/react-utils';
import { MENU_DELAY } from '../../../consts/common.js';
import { promoParam } from '../../../consts/links.js';
import { useAnalyticEvents } from '../../../hooks/useAnalyticEvents.js';
import { useCreateIntersectionObserverRefs } from '../../../hooks/useCreateIntersectionObserverRefs.js';
import { defaultDesktopMenu } from '../../../mocks/defaultDesktopMenu.js';
import { flatMenuLinks } from '../../../utils/menu.js';
import { getCustomStyles } from '../../../utils/styles.js';
import { HiddenLinks } from '../../HiddenLinks/src/HiddenLinks.js';
import { MenuItem } from '../../MenuItem/src/MenuItem.js';
import '../../Providers/CustomPropsProvider/src/CustomPropsProvider.js';
import { useCustomProps } from '../../Providers/CustomPropsProvider/hooks/useCustomProps.js';
import '../../Providers/MenuItemVisibleProvider/src/MenuItemVisibleProvider.js';
import { useMenuItemVisible } from '../../Providers/MenuItemVisibleProvider/hooks/useMenuItemVisible.js';
import { useHiddenTopLinksObserver } from '../hooks/useHiddenTopLinksObserver.js';
import modules_25250a06 from '../styles/DesktopMenu.module.less.js';
import { DesktopMenuDropdown } from './DesktopMenuDropdown.js';
import { DesktopMenuTopLink } from './DesktopMenuTopLink.js';

const DesktopMenu = (props) => {
    const { className, ...restProps } = props;
    const cx = useStyles(modules_25250a06);
    const { pushAnalyticEvent } = useAnalyticEvents();
    const { settings = {}, onOpen, onMenuOpen, onClose, onMenuClose } = useCustomProps();
    const { menu = defaultDesktopMenu } = settings;
    const [activeItem, setActiveItem] = useMenuItemVisible();
    const activeItemTextDebounced = useDebounceValue(activeItem, MENU_DELAY);
    const [isMoreTopLinksDialogVisible, setIsMoreTopLinksDialogVisible] = useState(false);
    const promoLinks = useMemo(() => flatMenuLinks(menu).filter((item) => item.href?.includes(promoParam)), [menu]);
    const handleTopLinkMouseEnter = useCallback((item) => () => {
        if (!item) {
            return;
        }
        setActiveItem(item);
        setIsMoreTopLinksDialogVisible(false);
        onOpen?.();
        onMenuOpen?.(item);
    }, [onMenuOpen, onOpen, setActiveItem]);
    const handleTopLinkMouseLeave = useCallback(() => {
        setActiveItem(undefined);
        onClose?.();
        onMenuClose?.();
    }, [onClose, onMenuClose, setActiveItem]);
    const handleMoreTopLinksTriggerClick = useCallback(() => setIsMoreTopLinksDialogVisible((isVisible) => !isVisible), []);
    const handleMoreTopLinksDialogCloseClick = useCallback(() => setIsMoreTopLinksDialogVisible(false), []);
    const handleMenuItemClick = useCallback((event) => {
        const label = event.currentTarget.dataset.label || '';
        pushAnalyticEvent("menuLinkClick" /* EVENTS.MENU_LINK_CLICK */, { eventLabel: `${activeItemTextDebounced}|${label}` });
    }, [activeItemTextDebounced, pushAnalyticEvent]);
    const handlePromoLinkIntersectionChange = useCallback((data) => {
        const { inView, item } = data;
        const { text, href } = item;
        if (inView) {
            pushAnalyticEvent("promoLinkInView" /* EVENTS.PROMO_LINK_IN_VIEW */, { eventLabel: `${activeItemTextDebounced}|${text}|${href}` });
        }
    }, [activeItemTextDebounced, pushAnalyticEvent]);
    const { refs: hiddenItemsIntersectionRefs, getRefKey: getHiddenItemsRefKey, hiddenItems, lastRightVisibleEntry, } = useHiddenTopLinksObserver({
        items: menu,
    });
    const { refs: promoIntersectionRefs, getRefKey: getPromoRefKey } = useCreateIntersectionObserverRefs({
        items: promoLinks,
        onChange: handlePromoLinkIntersectionChange,
    });
    // Затыкаю дырку с типизацией. найти лучшее решение ошибки
    // Type 'RefObject<HTMLElement>' is not assignable to type 'Ref<HTMLAnchorElement>'.
    const getMenuItemRef = useCallback((item) => promoIntersectionRefs[getPromoRefKey(item)], [getPromoRefKey, promoIntersectionRefs]);
    const isMoreButtonVisible = hiddenItems.length > 0;
    const moreButtonLeftCoordinate = useMemo(() => {
        const target = typeof lastRightVisibleEntry?.target !== 'undefined' ? lastRightVisibleEntry.target : undefined;
        if (target) {
            return target.offsetLeft + target.offsetWidth;
        }
        // Делаем кнопку невидимой
        return null;
    }, [lastRightVisibleEntry]);
    const linkStyle = useMemo(() => (moreButtonLeftCoordinate === null ? undefined : { left: `${moreButtonLeftCoordinate}px` }), [moreButtonLeftCoordinate]);
    useEffect(() => {
        if (activeItemTextDebounced)
            pushAnalyticEvent("topMenuShow" /* EVENTS.TOP_MENU_SHOW */, { eventLabel: `${activeItemTextDebounced}|Наведение` });
    }, [activeItemTextDebounced, pushAnalyticEvent]);
    return (React__default.createElement("div", { className: cx('container', className, { containerWithMore: isMoreButtonVisible }), ...restProps },
        React__default.createElement(HiddenLinks, { menu: menu, hideTopLinks: true }),
        menu.map((topLink) => {
            const hasWidget = Boolean(topLink.widgetSourceUrl);
            const isExistsMenuDropdown = (Array.isArray(topLink.children) && topLink.children.length > 0) || hasWidget;
            return (React__default.createElement(DesktopMenuTopLink, { ref: mergeRefs(hiddenItemsIntersectionRefs[getHiddenItemsRefKey(topLink)], promoIntersectionRefs[getPromoRefKey(topLink)]), className: cx('item'), key: topLink.text, item: topLink, style: getCustomStyles(topLink), onMouseEnter: isExistsMenuDropdown ? handleTopLinkMouseEnter(topLink.text) : undefined, onMouseLeave: isExistsMenuDropdown ? handleTopLinkMouseLeave : undefined }, topLink.text === activeItemTextDebounced && isExistsMenuDropdown && (React__default.createElement(DesktopMenuDropdown, { key: (topLink.href ?? '') + topLink.text, item: topLink, getRef: getMenuItemRef, isVisible: topLink.text === activeItemTextDebounced, widgetSourceUrl: topLink.widgetSourceUrl, onItemClick: handleMenuItemClick }))));
        }),
        React__default.createElement(Popover, { className: cx('moreTopLinksDialog'), visible: isMoreTopLinksDialogVisible, content: React__default.createElement(Space, { direction: "vertical" }, hiddenItems.map((item) => (React__default.createElement(MenuItem, { key: item.text, size: 16, item: item, className: cx('moreTopLink') })))), onClose: handleMoreTopLinksDialogCloseClick },
            React__default.createElement(Link, { strong: true, color: "dark", style: linkStyle, className: cx('itemMore', { itemMoreVisible: isMoreButtonVisible, hidden: !linkStyle }), onClick: handleMoreTopLinksTriggerClick }, "\u0415\u0449\u0451"))));
};
DesktopMenu.displayName = 'DesktopMenu';

export { DesktopMenu };
//# sourceMappingURL=DesktopMenu.js.map
